import React, { useState } from "react";
import { companyLabel, todayIso } from "./helpers";

const EMPTY = {
  company_id: "",
  discount_percent: "",
  valid_from: "",
  valid_to: "",
};

/**
 * Grants or amends one company discount.
 *
 * @param {object} props - Component props.
 * @param {object|null} props.editing - Discount being amended, or null to grant a new one.
 * @param {object[]} props.companies - Companies a discount may be granted to.
 * @param {Function} props.onSubmit - Called with the form body.
 * @param {Function} props.onCancel - Leaves edit mode.
 * @param {boolean} props.saving - Whether a save is in flight.
 * @returns {JSX.Element} The discount form.
 *
 * @remarks
 * Company is fixed once granted. Moving a concession between companies is two
 * decisions, and expressing it as an edit would leave no record that the first
 * company ever had one — so the field is read-only while amending.
 *
 * Chosen from a list rather than typed as an account id. A discount is money,
 * and a mistyped id lands one on the wrong company with nothing on the screen
 * to contradict it — the number looks exactly as valid as the right one.
 */
const DiscountForm = ({
  editing,
  companies = [],
  onSubmit,
  onCancel,
  saving,
}) => {
  const [draft, setDraft] = useState(
    editing
      ? {
          company_id: String(editing.companyId),
          discount_percent: editing.discountPercent,
          valid_from: String(editing.validFrom).slice(0, 10),
          valid_to: editing.validTo ? String(editing.validTo).slice(0, 10) : "",
        }
      : { ...EMPTY, valid_from: todayIso() }
  );

  const change = (field) => (event) =>
    setDraft({ ...draft, [field]: event.target.value });

  const submit = (event) => {
    event.preventDefault();

    const body = {
      discount_percent: draft.discount_percent,
      valid_from: draft.valid_from,
      valid_to: draft.valid_to === "" ? null : draft.valid_to,
    };

    onSubmit(editing ? body : { ...body, company_id: draft.company_id });
  };

  const complete =
    draft.discount_percent !== "" &&
    draft.valid_from !== "" &&
    (Boolean(editing) || draft.company_id !== "");

  return (
    <form className="aicr-card" onSubmit={submit}>
      <h2>{editing ? "Amend discount" : "Grant a discount"}</h2>
      <p className="aicr-card-sub">
        A discount reduces what a company pays to buy credits. It never changes
        the markup applied when they spend them — those are separate levers.
      </p>

      <div className="aicr-grid aicr-grid-discount">
        <div className="aicr-field">
          <label htmlFor="aicr-company">Company</label>
          <select
            id="aicr-company"
            value={draft.company_id}
            onChange={change("company_id")}
            disabled={Boolean(editing)}
          >
            {editing ? (
              <option value={draft.company_id}>
                {editing.companyName || `Company ${editing.companyId}`}
              </option>
            ) : (
              <>
                <option value="">Select a company…</option>
                {companies.map((company) => (
                  <option key={company.id} value={String(company.id)}>
                    {companyLabel(company)}
                  </option>
                ))}
              </>
            )}
          </select>
          <span className="aicr-hint">
            {editing
              ? "Cannot be changed once granted."
              : "Who the discount applies to."}
          </span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-discount">Discount (%)</label>
          <input
            id="aicr-discount"
            type="text"
            inputMode="decimal"
            value={draft.discount_percent}
            onChange={change("discount_percent")}
          />
          <span className="aicr-hint">Between 0.001 and 99.999.</span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-from">Valid from</label>
          <input
            id="aicr-from"
            type="date"
            value={draft.valid_from}
            onChange={change("valid_from")}
          />
          <span className="aicr-hint">Inclusive.</span>
        </div>

        <div className="aicr-field">
          <label htmlFor="aicr-to">Valid to</label>
          <input
            id="aicr-to"
            type="date"
            value={draft.valid_to}
            onChange={change("valid_to")}
          />
          <span className="aicr-hint">Leave empty for open-ended.</span>
        </div>
      </div>

      <div className="aicr-actions">
        <button
          type="submit"
          className="aicr-btn aicr-btn-primary"
          disabled={!complete || saving}
        >
          {saving ? "Saving…" : editing ? "Save changes" : "Grant discount"}
        </button>
        {editing && (
          <button
            type="button"
            className="aicr-btn aicr-btn-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default DiscountForm;
